import React from 'react';
import { Table, TableContainer } from '@mui/material';

// A table you can actually navigate.
//
// THE PROBLEM THIS FIXES
//
// A TableContainer with no height limit grows to fit every row, and the horizontal
// scrollbar belongs to the container — so it ends up below the last row. On a list of 500
// tenants you had to scroll to the bottom of the whole list before you could scroll right
// to reach the Actions column, and again every time you moved. Capping the height puts the
// scrollbar at the bottom of the visible box, where it always is, whether there are five
// rows or five thousand.
//
// `stickyHeader` follows from the same change: once the body scrolls inside a box, the
// column names have to stay put or you lose them on the second screenful.
//
// PINNING COLUMNS
//
// Add `className="pin-left"` to the cell that identifies the row — usually a name or a
// document number — and `className="pin-right"` to the actions cell. Scrolling sideways
// then never loses which row you are on, and never hides what you can do with it. Both
// header and body cells need the class.
//
//   <ScrollTable>
//     <TableHead><TableRow>
//       <TableCell className="pin-left">Name</TableCell>
//       ...
//       <TableCell className="pin-right">Actions</TableCell>
//     </TableRow></TableHead>
//     <TableBody>...</TableBody>
//   </ScrollTable>
//
// Written once rather than as an sx block copied into thirty-eight files, where the
// thirty-ninth would have been written without it.

// Leaves room for the page header, filter bar and pagination. A value rather than 100%
// because the container has to be shorter than the viewport for the scrollbar to sit
// inside it — that is the entire point.
const DEFAULT_MAX_HEIGHT = 'calc(100vh - 320px)';

// For a table that is NOT the page — one inside a dialog, an accordion panel, or stacked
// with others in a report card.
//
// The page default is measured against the viewport, which is wrong the moment the table
// is not the only thing filling it: inside a dialog it would be taller than the dialog, and
// three report tables in one card would each claim a screenful, so the card scrolls past
// two of them to reach the third. A fixed, modest height keeps each one self-contained and
// its own scrollbar where you can see it.
const EMBEDDED_MAX_HEIGHT = 420;

const pinnedStyles = {
  '& .pin-left': {
    position: 'sticky',
    left: 0,
    zIndex: 2,
    // Opaque: a transparent pinned cell lets the scrolling content slide visibly beneath
    // it, which looks like a rendering fault.
    bgcolor: 'background.paper',
    borderRight: '1px solid',
    borderColor: 'divider'
  },
  '& .pin-right': {
    position: 'sticky',
    right: 0,
    zIndex: 2,
    bgcolor: 'background.paper',
    borderLeft: '1px solid',
    borderColor: 'divider'
  },
  // The header corners are pinned in both directions at once and must sit above the pinned
  // body cells as they scroll underneath.
  '& thead .pin-left, & thead .pin-right': { zIndex: 4 }
};

const ScrollTable = ({
  children,
  maxHeight = DEFAULT_MAX_HEIGHT,
  containerProps = {},
  sx = {},
  ...tableProps
}) => {
  const { sx: containerSx, ...restContainer } = containerProps;
  return (
    <TableContainer {...restContainer} sx={{ maxHeight, overflow: 'auto', ...(containerSx || {}) }}>
      <Table stickyHeader sx={{ ...pinnedStyles, ...sx }} {...tableProps}>
        {children}
      </Table>
    </TableContainer>
  );
};

export default ScrollTable;
export { DEFAULT_MAX_HEIGHT, EMBEDDED_MAX_HEIGHT, pinnedStyles };
